import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormControlLabel, FormHelperText, Radio, RadioGroup } from "@mui/material";
import Button from "@components/Button/Button";
import FormItem from "@components/FormItem/FormItem";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import FormSection from "@components/FormSection/FormSection";
import Input from "@components/Input/Input";
import { useNotification } from "@context/NotificationContext";
import { useVisitorDetailsReferenceData } from "@features/gatepass/hooks/useVisitorDetailsReferenceData";
import { isDateOnOrAfterToday } from "@features/gatepass/schema/gatepassScValidity.utils";
import type { TruckGatepassFormData } from "@features/gatepass/schema/truckGatepassFormSchema";
import { formatOnChangeEmiratesIDNumber } from "@features/port-security/components/utils";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { resolveGatepassRegistrationEmiratePrefill } from "@services/referenceDataApi";
import {
  checkGatepassSecurityClearance,
  getMySecurityClearance,
  getSecurityClearanceById,
} from "@services/securityClearanceApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import { runFieldNavigation } from "@telemetry/runFieldNavigation";
import { runInquiry } from "@telemetry/runInquiry";
import { paths } from "@utils/navigation";
import { redirectForTranslateFromEnglishToArabic } from "@utils/translateFromEnglishToArabic.ts";
import { t } from "i18next";
import { Controller, FormProvider } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { useTruckGatepassFormStore } from "../../store/useTruckGatepassForm.store";
import SecurityClearanceStatusMessage from "../NormalGatepass/SecurityClearanceStatusMessage";
import {
  extractScErrorDetail,
  formatDateToDdMmYyyy,
  formatPortOptions,
  requiresManualSecurityClearanceCheck,
  resolveScSearchField,
} from "../utils";
import type { TruckGatepassOutletContext, TruckVehicleFormData } from "./types";

const TRANSLATION_KEY = "gatepass";
const gatepassService = "bff-gatepass";
const INDIVIDUAL_ROLES = ["security-applicant", "uae-pass-user"];
const SECURITY_CLEARANCE_CHECK_INQUIRY_CONTEXT = {
  module: "gatepass",
  screen: "Truck Gatepass Visitor Details",
  action: "check_security_clearance",
  entityType: "security_clearance",
};
const SECURITY_CLEARANCE_FIELD_NAVIGATION_CONTEXT = {
  module: "gatepass",
  screen: "Truck Gatepass Visitor Details",
  action: "populate_security_clearance_from_document_id",
  fromField: "passportNumber_or_emiratesIdNumber",
  toField: "securityClearance",
  entityType: "security_clearance",
};

const createEmptyVehicle = (): TruckVehicleFormData => ({
  registrationEmirate: "Fujairah",
  vehicleType: "",
  vehicleColour: "",
  category: "",
  plateNumber: "",
  drivers: [
    {
      driverLicenseNumber: "",
      fullName: "",
      passportNumber: "",
      emiratesIdNumber: "",
      mobileNumber: "",
      securityClearanceId: "",
      securityClearanceValidity: "",
      securityClearanceFott: "",
    },
  ],
});

const createUpdatedAutoPopulatedDrivers = (
  vehicles: TruckVehicleFormData[],
  values: {
    driverLicenseNumber?: string;
    emiratesIdNumber: string;
    fullName: string;
    mobileNumber: string;
    passportNumber: string;
    plateNumber?: string;
    registrationEmirate?: string;
    securityClearanceId: string;
    securityClearanceValidity?: string;
    securityClearanceFott?: string;
    vehicleType?: string;
  },
  options: { isIndividualRole: boolean },
): TruckVehicleFormData[] =>
  vehicles.map((vehicle, vehicleIndex) => {
    if (!options.isIndividualRole && vehicleIndex > 0) {
      return vehicle;
    }

    const currentDrivers = Array.isArray(vehicle.drivers) ? vehicle.drivers : [];
    const currentFirstDriver = currentDrivers[0] ?? createEmptyVehicle().drivers[0];

    return {
      ...vehicle,
      registrationEmirate:
        vehicleIndex === 0
          ? (values.registrationEmirate ?? vehicle.registrationEmirate)
          : vehicle.registrationEmirate,
      vehicleType:
        vehicleIndex === 0 ? (values.vehicleType ?? vehicle.vehicleType) : vehicle.vehicleType,
      plateNumber:
        vehicleIndex === 0 ? (values.plateNumber ?? vehicle.plateNumber) : vehicle.plateNumber,
      drivers: [
        {
          ...currentFirstDriver,
          fullName: values.fullName,
          passportNumber: values.passportNumber,
          emiratesIdNumber: values.emiratesIdNumber,
          mobileNumber: values.mobileNumber,
          securityClearanceId: values.securityClearanceId,
          securityClearanceValidity:
            values.securityClearanceValidity ?? currentFirstDriver.securityClearanceValidity ?? "",
          securityClearanceFott:
            values.securityClearanceFott ?? currentFirstDriver.securityClearanceFott ?? "",
          ...(values.driverLicenseNumber
            ? { driverLicenseNumber: values.driverLicenseNumber }
            : {}),
        },
        ...currentDrivers.slice(1),
      ],
    };
  });

const VisitorDetails = () => {
  const { formData, updateFormData } = useOutletContext<TruckGatepassOutletContext>();
  const { ports, isLoadingPorts, gates, isLoadingGates, companies, fottDisplayOptions } =
    useVisitorDetailsReferenceData({ gatepassService });

  const [isCheckingPassport, setIsCheckingPassport] = useState(false);
  const [lastSearchField, setLastSearchField] = useState<"passport" | "emirates" | null>(null);
  const { notify } = useNotification();
  const [scCheckErrorMessage, setScCheckErrorMessage] = useState("");
  const { setField } = useTruckGatepassFormStore();
  const userRoles = useAuthStore((state) => state.user?.roles ?? []);
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const bootstrapSecurityClearanceId = useAuthStore((state) => state.bootstrapSecurityClearanceId);
  const isIndividualRole = INDIVIDUAL_ROLES.some((role) => userRoles.includes(role));
  const requiresManualScCheck = requiresManualSecurityClearanceCheck(userRoles);

  const { data: latestApprovedScId, isPending: isLoadingLatestApprovedSc } = useQuery({
    queryKey: ["gatepass-individual-my-sc", userId],
    queryFn: async () => {
      try {
        const me = await getMySecurityClearance({ service: gatepassService });
        const id = String(me?.id ?? "").trim();
        return id || null;
      } catch {
        return null;
      }
    },
    enabled: isIndividualRole && Boolean(userId),
    staleTime: 60 * 1000,
  });
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const isPassportValid = useMemo(
    () => (value: string) => /^[a-zA-Z0-9]{6,10}$/.test(String(value ?? "").trim()),
    [],
  );
  const isEmiratesIdValid = useMemo(
    () => (value: string) => {
      const raw = String(value ?? "").trim();
      if (!raw) return false;
      if (!/^[0-9-]+$/.test(raw)) return false;
      const digitsOnly = raw.replace(/\D/g, "");
      return digitsOnly.length === 15;
    },
    [],
  );
  const previousPassportRef = useRef("");
  const previousEmiratesIdRef = useRef("");

  const syncScFields = useCallback(
    (
      values: Partial<
        Pick<TruckGatepassFormData, "SCStatus" | "securityClearanceSerial" | "securityClearanceId">
      >,
      options?: { shouldValidate?: boolean },
    ) => {
      const shouldValidate = options?.shouldValidate ?? false;
      (
        Object.entries(values) as Array<
          [
            keyof Pick<
              TruckGatepassFormData,
              "SCStatus" | "securityClearanceSerial" | "securityClearanceId"
            >,
            string | undefined,
          ]
        >
      ).forEach(([key, value]) => {
        if (value === undefined) return;
        formData.setValue(key, value, { shouldDirty: true, shouldValidate });
        setField(key, value);
        updateFormData(key, value);
      });
    },
    [formData, setField, updateFormData],
  );

  const syncTextField = useCallback(
    (field: keyof TruckGatepassFormData, value: string, options?: { shouldValidate?: boolean }) => {
      const shouldValidate = options?.shouldValidate ?? false;
      formData.setValue(field, value, { shouldDirty: true, shouldValidate });
      setField(field, value);
      updateFormData(field, value);
    },
    [formData, setField, updateFormData],
  );

  const clearScDerivedFields = useCallback(() => {
    syncTextField("fullName", "", { shouldValidate: false });
    syncTextField("nationality", "", { shouldValidate: false });
    syncTextField("passportNumberExpirationDate", "", { shouldValidate: false });
    syncTextField("driverLicenseNumber", "", { shouldValidate: false });
    syncTextField("phoneNumber", "", { shouldValidate: false });
    syncTextField("sponsorCompany", "", { shouldValidate: false });
    syncTextField("visitorCompanyEnglish", "", { shouldValidate: false });
    syncTextField("visitorCompanyArabic", "", { shouldValidate: false });
    const currentVehicles = formData.getValues("vehicles") ?? [];
    const baseVehicles =
      Array.isArray(currentVehicles) && currentVehicles.length > 0
        ? currentVehicles
        : [createEmptyVehicle()];

    const resetVehicles: TruckVehicleFormData[] = baseVehicles.map((vehicle, vehicleIndex) => {
      if (vehicleIndex !== 0) {
        return vehicle;
      }

      const currentDrivers = Array.isArray(vehicle.drivers) ? vehicle.drivers : [];
      const firstDriver = currentDrivers[0] ?? createEmptyVehicle().drivers[0];

      return {
        ...vehicle,
        vehicleType: "",
        plateNumber: "",
        drivers: [
          {
            ...firstDriver,
            driverLicenseNumber: "",
            fullName: "",
            passportNumber: "",
            emiratesIdNumber: "",
            mobileNumber: "",
            securityClearanceId: "",
          },
          ...currentDrivers.slice(1),
        ],
      };
    });

    formData.setValue("vehicles", resetVehicles, {
      shouldDirty: true,
      shouldValidate: false,
    });
    setField("vehicles", resetVehicles);
    updateFormData("vehicles", resetVehicles);
    syncTextField("securityClearanceValidity", "", { shouldValidate: false });
    syncTextField("securityClearanceFott", "", { shouldValidate: false });
  }, [formData, setField, syncTextField, updateFormData]);

  const clearFieldValue = useCallback(
    (field: "passportNumber" | "emiratesIdNumber") => {
      formData.setValue(field, "", { shouldDirty: true, shouldValidate: true });
      updateFormData(field, "");
      if (field === "passportNumber") {
        previousPassportRef.current = "";
      } else {
        previousEmiratesIdRef.current = "";
      }
    },
    [formData, updateFormData],
  );

  const resetScLookupState = useCallback(() => {
    setLastSearchField(null);
    setScCheckErrorMessage("");
    syncScFields(
      {
        SCStatus: "",
        securityClearanceSerial: "",
        securityClearanceId: "",
      },
      { shouldValidate: false },
    );
  }, [syncScFields]);

  const checkPassportMutation = useMutation({
    mutationFn: async ({
      passportNumber,
      emiratesIdNumber,
      trackInquiry,
    }: {
      passportNumber?: string;
      emiratesIdNumber?: string;
      trackInquiry?: boolean;
    }) => {
      const hasPassport = Boolean(passportNumber?.trim());
      const hasEmiratesId = Boolean(emiratesIdNumber?.trim());
      if (!hasPassport && !hasEmiratesId) return null;
      const execute = () =>
        checkGatepassSecurityClearance({
          service: gatepassService,
          ...(hasPassport ? { passportNumber } : {}),
          ...(hasEmiratesId ? { emiratesId: emiratesIdNumber } : {}),
        });

      if (trackInquiry) {
        return runFieldNavigation(SECURITY_CLEARANCE_FIELD_NAVIGATION_CONTEXT, () =>
          runInquiry(SECURITY_CLEARANCE_CHECK_INQUIRY_CONTEXT, execute),
        );
      }

      return execute();
    },
    onSuccess: (response, variables) => {
      setScCheckErrorMessage("");
      if (!response) {
        syncScFields({
          SCStatus: "NOT_FOUND",
          securityClearanceSerial: "",
          securityClearanceId: "",
        });
        syncTextField("securityClearanceValidity", "", { shouldValidate: false });
        syncTextField("securityClearanceFott", "", { shouldValidate: false });
        setIsCheckingPassport(false);
        return;
      }

      const scResponse = response;
      const passportNumber = variables.passportNumber?.trim();
      const emiratesIdNumber = variables.emiratesIdNumber?.trim();
      setLastSearchField(resolveScSearchField(passportNumber, emiratesIdNumber));
      const statusUpper = String(scResponse.status ?? "").toUpperCase();
      const hasScRecord = Boolean(scResponse.securityClearanceId || scResponse.status);

      const scValidFlagProvided = typeof scResponse.valid === "boolean";
      const isApproved = statusUpper === "APPROVED";
      const expiryDateRaw = scResponse.expiryDate ?? scResponse.expirationDate;
      const isNotExpired =
        typeof expiryDateRaw === "string" && expiryDateRaw.trim()
          ? isDateOnOrAfterToday(expiryDateRaw)
          : true;
      const isEligible =
        hasScRecord &&
        (scValidFlagProvided ? scResponse.valid === true : isApproved && isNotExpired);

      if (!isEligible) {
        syncScFields({
          SCStatus: "NOT_FOUND",
          securityClearanceSerial: "",
          securityClearanceId: "",
        });
        syncTextField("securityClearanceValidity", "", { shouldValidate: false });
        syncTextField("securityClearanceFott", "", { shouldValidate: false });
        setIsCheckingPassport(false);
        return;
      }

      const foundSecurityClearanceId = String(scResponse.securityClearanceId ?? "").trim();
      const responsePassportNumber = String(
        scResponse.passportNumber ?? passportNumber ?? "",
      ).trim();
      const responseEmiratesIdNumber = String(
        scResponse.emiratesIdNumber ?? emiratesIdNumber ?? "",
      ).trim();

      syncScFields({
        SCStatus: statusUpper || "APPROVED",
        securityClearanceSerial: String(scResponse.serialNo ?? scResponse.serialNumber ?? ""),
        securityClearanceId: foundSecurityClearanceId,
      });
      syncTextField("securityClearanceValidity", formatDateToDdMmYyyy(scResponse.expiryDate));
      const fottFromSc = scResponse.fott === true ? t("yes") : t("no");
      syncTextField("securityClearanceFott", fottFromSc);
      formData.clearErrors(["passportNumber", "emiratesIdNumber", "securityClearanceId"]);

      if (responsePassportNumber) {
        syncTextField("passportNumber", responsePassportNumber);
        previousPassportRef.current = responsePassportNumber;
      }

      if (responseEmiratesIdNumber) {
        syncTextField("emiratesIdNumber", responseEmiratesIdNumber);
        previousEmiratesIdRef.current = responseEmiratesIdNumber;
      }

      if (!isIndividualRole) {
        if (
          passportNumber &&
          responseEmiratesIdNumber &&
          !formData.getValues("emiratesIdNumber")?.trim()
        ) {
          syncTextField("emiratesIdNumber", responseEmiratesIdNumber);
        }
        if (
          emiratesIdNumber &&
          responsePassportNumber &&
          !formData.getValues("passportNumber")?.trim()
        ) {
          syncTextField("passportNumber", responsePassportNumber);
        }
      }

      const responseFullName = String(scResponse.fullNameEnglish ?? "").trim();
      if (!formData.getValues("fullName") && responseFullName)
        syncTextField("fullName", responseFullName);
      const responseNationality = String(scResponse.presentNationality ?? "").trim();
      if (responseNationality) syncTextField("nationality", responseNationality);
      syncTextField(
        "passportNumberExpirationDate",
        formatDateToDdMmYyyy(scResponse.passportExpiryDate),
      );
      const drivingLicenseNumber = String(
        scResponse.vehicleDetails?.drivingLicenseNumber ?? "",
      ).trim();
      const registrationEmirate = resolveGatepassRegistrationEmiratePrefill(
        scResponse.vehicleDetails?.placeOfIssue,
      );
      const vehicleType = String(scResponse.vehicleDetails?.type ?? "").trim();
      const vehiclePlateNumber = String(scResponse.vehicleDetails?.plateNo ?? "").trim();
      if (drivingLicenseNumber) {
        syncTextField("driverLicenseNumber", drivingLicenseNumber);
      }
      syncTextField("phoneNumber", String(scResponse.mobileNumber ?? "").trim());
      const sponsorCompanyValue = String(scResponse.requestingSponsorEnglish ?? "").trim();
      if (sponsorCompanyValue) syncTextField("sponsorCompany", sponsorCompanyValue);
      const visitorCompanyEnglish = String(scResponse.visitorCompanyNameEnglish ?? "").trim();
      if (visitorCompanyEnglish) syncTextField("visitorCompanyEnglish", visitorCompanyEnglish);
      const visitorCompanyArabic = String(scResponse.visitorCompanyNameArabic ?? "").trim();
      if (visitorCompanyArabic) syncTextField("visitorCompanyArabic", visitorCompanyArabic);

      const currentVehicles = formData.getValues("vehicles") ?? [];
      const baseVehicles =
        Array.isArray(currentVehicles) && currentVehicles.length > 0
          ? currentVehicles
          : [createEmptyVehicle()];

      const vehiclesWithDriverUpdates = createUpdatedAutoPopulatedDrivers(
        baseVehicles,
        {
          driverLicenseNumber: drivingLicenseNumber || undefined,
          emiratesIdNumber: responseEmiratesIdNumber,
          fullName: responseFullName,
          mobileNumber: String(scResponse.mobileNumber ?? "").trim(),
          passportNumber: responsePassportNumber,
          plateNumber: vehiclePlateNumber || undefined,
          registrationEmirate,
          securityClearanceId: foundSecurityClearanceId,
          securityClearanceValidity: formatDateToDdMmYyyy(scResponse.expiryDate),
          securityClearanceFott: fottFromSc,
          vehicleType: vehicleType || undefined,
        },
        { isIndividualRole },
      );

      formData.setValue("vehicles", vehiclesWithDriverUpdates, {
        shouldDirty: true,
        shouldValidate: true,
      });
      formData.clearErrors("vehicles.0.registrationEmirate");
      setField("vehicles", vehiclesWithDriverUpdates);
      updateFormData("vehicles", vehiclesWithDriverUpdates);

      if (scResponse.fott !== undefined) {
        const fottValue = scResponse.fott === true;
        const nextFottValue = fottValue;
        formData.setValue("FOTTEntry", nextFottValue, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setField("FOTTEntry", nextFottValue);
        updateFormData("FOTTEntry", nextFottValue);
        formData.setValue("isFottDisabled", !fottValue, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setField("isFottDisabled", !fottValue);
        updateFormData("isFottDisabled", !fottValue);
      }
      setIsCheckingPassport(false);
    },
    onError: (error) => {
      const errorDetail = extractScErrorDetail(error);
      setScCheckErrorMessage(errorDetail);
      setLastSearchField(null);
      syncScFields({
        SCStatus: "",
        securityClearanceSerial: "",
        securityClearanceId: "",
      });
      syncTextField("securityClearanceValidity", "", { shouldValidate: false });
      syncTextField("securityClearanceFott", "", { shouldValidate: false });
      setIsCheckingPassport(false);
    },
  });

  useEffect(() => {
    if (!isIndividualRole || isLoadingLatestApprovedSc) return;
    const effectiveScId =
      (latestApprovedScId ?? "").trim() || (bootstrapSecurityClearanceId ?? "").trim();
    if (!effectiveScId) return;
    if (String(formData.getValues("securityClearanceId") ?? "").trim()) return;

    let cancelled = false;
    void (async () => {
      setIsCheckingPassport(true);
      try {
        const sc = await getSecurityClearanceById({
          id: effectiveScId,
          service: gatepassService,
        });
        if (cancelled) return;
        if (String(formData.getValues("securityClearanceId") ?? "").trim()) {
          setIsCheckingPassport(false);
          return;
        }
        const p = String(sc.applicant?.passportNumber ?? "").trim();
        const e = String(sc.applicant?.emiratesIdNumber ?? "").trim();
        if (!p && !e) {
          notify({
            title: t(`${TRANSLATION_KEY}.notifications.passport-check-error-title`),
            description: t(`${TRANSLATION_KEY}.notifications.passport-check-error-description`),
            type: "error",
          });
          setIsCheckingPassport(false);
          return;
        }
        syncTextField("passportNumber", p, { shouldValidate: false });
        syncTextField("emiratesIdNumber", e, { shouldValidate: false });
        previousPassportRef.current = p;
        previousEmiratesIdRef.current = e;
        checkPassportMutation.reset();
        checkPassportMutation.mutate({
          ...(p ? { passportNumber: p } : {}),
          ...(e ? { emiratesIdNumber: e } : {}),
        });
      } catch {
        if (!cancelled) {
          notify({
            title: t(`${TRANSLATION_KEY}.notifications.passport-check-error-title`),
            description: t(`${TRANSLATION_KEY}.notifications.passport-check-error-description`),
            type: "error",
          });
          setIsCheckingPassport(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formData.getValues is stable; omitting formData avoids spurious re-runs
  }, [
    bootstrapSecurityClearanceId,
    checkPassportMutation,
    isIndividualRole,
    isLoadingLatestApprovedSc,
    latestApprovedScId,
    notify,
    syncTextField,
  ]);

  const handleIdChange = useCallback(
    (passportValue: string, emiratesIdValue: string, preferredField?: "passport" | "emirates") => {
      if (debounceTimer) clearTimeout(debounceTimer);
      const timer = setTimeout(() => {
        const passportNumber = passportValue?.trim() ?? "";
        const emiratesIdNumber = emiratesIdValue?.trim() ?? "";
        const hasValidPassport = isPassportValid(passportNumber);
        const hasValidEmiratesId = isEmiratesIdValid(emiratesIdNumber);
        const shouldCheckPassport =
          preferredField === "passport"
            ? hasValidPassport
            : preferredField === "emirates"
              ? !hasValidEmiratesId && hasValidPassport
              : hasValidPassport;
        const shouldCheckEmiratesId =
          preferredField === "emirates"
            ? hasValidEmiratesId
            : preferredField === "passport"
              ? !hasValidPassport && hasValidEmiratesId
              : !hasValidPassport && hasValidEmiratesId;
        if (shouldCheckPassport || shouldCheckEmiratesId) {
          setIsCheckingPassport(true);
          setScCheckErrorMessage("");
          checkPassportMutation.reset();
          checkPassportMutation.mutate({
            ...(shouldCheckPassport ? { passportNumber } : {}),
            ...(shouldCheckEmiratesId ? { emiratesIdNumber } : {}),
            trackInquiry: true,
          });
        } else {
          resetScLookupState();
        }
      }, 500);
      setDebounceTimer(timer);
    },
    [checkPassportMutation, debounceTimer, isEmiratesIdValid, isPassportValid, resetScLookupState],
  );

  useEffect(() => {
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [debounceTimer]);

  const formattedPortOptions = useMemo(() => formatPortOptions(ports), [ports]);
  const sponsorCompanies = useMemo(
    () =>
      companies.map(({ companyNameEnglish, companyCode }) => ({
        label: companyNameEnglish,
        value: companyCode,
      })),
    [companies],
  );
  const portValue = formData.watch("port");
  const gateValue = formData.watch("gate");
  const selectedPort = ports.find(
    (port) => String(port.portCode).trim() === (portValue?.trim() ?? ""),
  );
  const selectedPortCode = selectedPort ? String(selectedPort.portCode).trim() : "";
  const sponsorGates = useMemo(() => {
    if (!selectedPortCode) return [];
    return gates
      .filter((gate) => String(gate.portCode).trim() === selectedPortCode)
      .map(({ gateDesc, gateNo }) => ({ label: gateDesc, value: gateNo }));
  }, [gates, selectedPortCode]);
  const allowedGateValues = useMemo(
    () => new Set(sponsorGates.map((g) => String(g.value))),
    [sponsorGates],
  );
  const passportValue = formData.watch("passportNumber");
  const emiratesIdValue = formData.watch("emiratesIdNumber");
  const visitorCompanyEnglish = formData.watch("visitorCompanyEnglish");
  const hasValidPassport = isPassportValid(passportValue ?? "");
  const hasValidEmiratesId = isEmiratesIdValid(emiratesIdValue ?? "");
  const scStatus = formData.watch("SCStatus") ?? "";
  const securityClearanceSerial = formData.watch("securityClearanceSerial") ?? "";
  const securityClearanceId = formData.watch("securityClearanceId") ?? "";
  const hasIdsForCheck = hasValidPassport || hasValidEmiratesId;
  const canClickCheckSc = !isCheckingPassport && hasIdsForCheck;
  const isGateDisabled = !portValue?.trim() || !selectedPortCode;
  const hasIdErrors = Boolean(
    formData.formState.errors.passportNumber || formData.formState.errors.emiratesIdNumber,
  );
  const securityClearanceError = formData.formState.errors.securityClearanceId?.message;
  const showSecurityClearanceError =
    Boolean(securityClearanceError) && formData.formState.submitCount > 0 && !scStatus;

  const handleSerialClick = useCallback(() => {
    if (!securityClearanceId) return;
    const url = `/${paths.portSecurity.index}/${paths.portSecurity.securityClearanceRequestPreview}/${securityClearanceId}`;
    window.open(url, "_blank");
  }, [securityClearanceId]);

  const handleManualScCheck = useCallback(() => {
    handleIdChange(passportValue ?? "", emiratesIdValue ?? "");
  }, [emiratesIdValue, handleIdChange, passportValue]);

  useEffect(() => {
    const currentGate = gateValue?.trim();
    if (!currentGate) return;

    if (isLoadingPorts || isLoadingGates) return;

    if (!portValue?.trim() || !allowedGateValues.has(currentGate)) {
      formData.setValue("gate", "", { shouldDirty: true, shouldValidate: true });
      updateFormData("gate", "");
    }
  }, [
    allowedGateValues,
    formData,
    gateValue,
    portValue,
    updateFormData,
    isLoadingPorts,
    isLoadingGates,
  ]);

  const visitorScValidityError = formData.formState.errors.securityClearanceValidity?.message;

  return (
    <FormProvider {...formData}>
      <FormSection
        title={t(`${TRANSLATION_KEY}.visitor-details`)}
        headerError={visitorScValidityError ? String(visitorScValidityError) : undefined}
      >
        <FormItem<TruckGatepassFormData, "passportNumber">
          required={!emiratesIdValue?.trim()}
          name="passportNumber"
          type="text"
          label={t(`${TRANSLATION_KEY}.passport-number`)}
          testId="truckGatepass-passportNumber"
          disabled={isIndividualRole}
          onFieldChange={(key, value) => {
            const nextPassport = String(value ?? "").trim();
            const currentPassport = previousPassportRef.current;
            const hasChanged = nextPassport !== currentPassport;
            previousPassportRef.current = nextPassport;
            updateFormData(key, value);
            if (hasChanged && formData.getValues("emiratesIdNumber")?.trim()) {
              clearFieldValue("emiratesIdNumber");
            }
            if (hasChanged) {
              clearScDerivedFields();
              resetScLookupState();
              formData.clearErrors("securityClearanceId");
            }
          }}
          enforceUppercase
        />
        <FormItem<TruckGatepassFormData, "emiratesIdNumber">
          required={!passportValue?.trim()}
          name="emiratesIdNumber"
          type="text"
          label={t(`${TRANSLATION_KEY}.emirates-id-number`)}
          testId="truckGatepass-emiratesIdNumber"
          formatOnChange={formatOnChangeEmiratesIDNumber}
          disabled={isIndividualRole}
          onFieldChange={(key, value) => {
            const nextEmiratesId = String(value ?? "").trim();
            const currentEmiratesId = previousEmiratesIdRef.current;
            const hasChanged = nextEmiratesId !== currentEmiratesId;
            previousEmiratesIdRef.current = nextEmiratesId;
            updateFormData(key, value);
            if (hasChanged && formData.getValues("passportNumber")?.trim()) {
              clearFieldValue("passportNumber");
            }
            if (hasChanged) {
              clearScDerivedFields();
              resetScLookupState();
              formData.clearErrors("securityClearanceId");
            }
          }}
        />
        <div className={`col-span-2 ${hasIdErrors ? "" : "-mt-4"}`}>
          <SecurityClearanceStatusMessage
            isChecking={isCheckingPassport}
            status={scStatus}
            serial={securityClearanceSerial}
            errorMessage={scCheckErrorMessage}
            lastSearchField={lastSearchField}
            onSerialClick={handleSerialClick}
            translationKey={TRANSLATION_KEY}
          />
          {showSecurityClearanceError && (
            <FormHelperText error>{String(securityClearanceError)}</FormHelperText>
          )}
        </div>
        {requiresManualScCheck && (
          <div className="mb-4 col-span-2">
            <Button
              testId="truckGatepass-checkSc"
              type="button"
              variant="outlined"
              color="primary"
              className="bg-white"
              sx={{ textTransform: "none" }}
              disabled={!canClickCheckSc}
              onClick={handleManualScCheck}
              label={t(`${TRANSLATION_KEY}.check-security-clearance`)}
            />
          </div>
        )}
        <div className="col-span-2 flex flex-col gap-4 pb-7 md:flex-row">
          <div className="flex min-w-0 flex-1 flex-col">
            <CustomFormLabel sx={{ mb: 1 }} disabled>
              {t("gatepass.security-clearance-validity", {
                defaultValue: "Security clearance validity",
              })}
            </CustomFormLabel>
            <div className="mt-auto w-full">
              <Controller
                name="securityClearanceValidity"
                control={formData.control}
                render={({ field: inputField }) => (
                  <Input
                    {...inputField}
                    fullWidth
                    disabled
                    testId="truckGatepass-securityClearanceValidity"
                    placeholder="-"
                  />
                )}
              />
            </div>
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <CustomFormLabel sx={{ mb: 1 }} disabled>
              {t("gatepass.fott", { defaultValue: "FOTT" })}
            </CustomFormLabel>
            <div className="mt-auto w-full">
              <Controller
                name="securityClearanceFott"
                control={formData.control}
                render={({ field: inputField }) => (
                  <RadioGroup
                    row
                    {...inputField}
                    value={inputField.value ?? ""}
                    onChange={(e) => inputField.onChange(e.target.value)}
                    sx={{
                      "display": "flex",
                      "flexDirection": "row",
                      "flexWrap": "wrap",
                      "columnGap": 2,
                      "rowGap": 0.5,
                      ".MuiFormControlLabel-root": {
                        marginRight: 0,
                      },
                    }}
                  >
                    {fottDisplayOptions.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio />}
                        label={option.label}
                        disabled
                      />
                    ))}
                  </RadioGroup>
                )}
              />
            </div>
          </div>
        </div>

        <FormItem<TruckGatepassFormData, "fullName">
          required
          name="fullName"
          type="text"
          label={t(`${TRANSLATION_KEY}.fullName-en`)}
          testId="truckGatepass-fullName"
          disabled
          onFieldChange={updateFormData}
        />
        <FormItem<TruckGatepassFormData, "nationality">
          name="nationality"
          type="text"
          label={t(`${TRANSLATION_KEY}.nationality`)}
          testId="truckGatepass-nationality"
          disabled
          onFieldChange={updateFormData}
        />
        <FormItem<TruckGatepassFormData, "passportNumberExpirationDate">
          name="passportNumberExpirationDate"
          type="text"
          label={t(`${TRANSLATION_KEY}.passport-number-expiration-date`)}
          testId="truckGatepass-passportNumberExpirationDate"
          disabled
          onFieldChange={updateFormData}
        />
        <FormItem<TruckGatepassFormData, "phoneNumber">
          required
          name="phoneNumber"
          type="text"
          label={t(`${TRANSLATION_KEY}.phone-number`)}
          testId="truckGatepass-phoneNumber"
          onFieldChange={updateFormData}
        />
        <FormItem<TruckGatepassFormData, "sponsorCompany">
          required
          name="sponsorCompany"
          type="autocomplete"
          options={sponsorCompanies}
          label={t(`${TRANSLATION_KEY}.sponsor-company`)}
          testId="truckGatepass-sponsorCompany"
          onFieldChange={updateFormData}
        />
        <FormItem<TruckGatepassFormData, "expectedEntryDate">
          required
          name="expectedEntryDate"
          type="date-picker"
          label={t(`${TRANSLATION_KEY}.expected-entry-date`)}
          testId="truckGatepass-expectedEntryDate"
          onFieldChange={updateFormData}
          dateConstraint="not-past"
          timePicker={false}
        />
        <FormItem<TruckGatepassFormData, "port">
          required
          name="port"
          type="autocomplete"
          options={formattedPortOptions}
          label={t(`${TRANSLATION_KEY}.port`)}
          inputLabel={t(`${TRANSLATION_KEY}.port-placeholder`)}
          testId="truckGatepass-port"
          onFieldChange={updateFormData}
        />
        <FormItem<TruckGatepassFormData, "gate">
          required
          name="gate"
          type="autocomplete"
          options={sponsorGates}
          label={t(`${TRANSLATION_KEY}.gate-selection`)}
          testId="truckGatepass-gate"
          disabled={isGateDisabled}
          onFieldChange={updateFormData}
        />
        <div className="col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormItem<TruckGatepassFormData, "visitorCompanyEnglish">
            required
            name="visitorCompanyEnglish"
            type="text"
            label={t(`${TRANSLATION_KEY}.visitor-company-name-en`)}
            testId="truckGatepass-visitorCompanyEnglish"
            onFieldChange={updateFormData}
          />
          <FormItem<TruckGatepassFormData, "visitorCompanyArabic">
            required
            name="visitorCompanyArabic"
            type="text"
            dir="rtl"
            label={t(`${TRANSLATION_KEY}.visitor-company-name-ar`)}
            testId="truckGatepass-visitorCompanyArabic"
            actionInLabel
            textForActionInLabel={t("security-clearance.translate")}
            onActionClicked={() =>
              redirectForTranslateFromEnglishToArabic(visitorCompanyEnglish ?? "")
            }
            onFieldChange={updateFormData}
          />
        </div>
        <FormItem<TruckGatepassFormData, "reference">
          name="reference"
          type="text"
          label={t(`${TRANSLATION_KEY}.reference`)}
          testId="truckGatepass-reference"
          onFieldChange={updateFormData}
        />
      </FormSection>
    </FormProvider>
  );
};

export default VisitorDetails;
